import React, { createContext, useContext, useState, useCallback } from 'react';

/**
 * UndoRedoContext
 * 
 * Allows the undo/redo state from SprintDetail to be consumed by the Sidebar.
 * SprintDetail provides the actual state, Sidebar consumes it for the history buttons.
 */

const UndoRedoContext = createContext({
  // State
  canUndo: false,
  canRedo: false,
  undoCount: 0,
  redoCount: 0,
  isHistoryOpen: false,
  // Handlers
  onUndo: () => {},
  onRedo: () => {},
  onToggleHistory: () => {},
  onOpenMissionSearch: null,
  // Whether a campaign/template is active (to show/hide the section)
  isActive: false,
});

/**
 * Provider component - wraps the app to allow state sharing
 */
export function UndoRedoProvider({ children }) {
  const [state, setState] = useState({
    canUndo: false,
    canRedo: false,
    undoCount: 0,
    redoCount: 0,
    isHistoryOpen: false,
    onUndo: () => {},
    onRedo: () => {},
    onToggleHistory: () => {},
    onOpenMissionSearch: null,
    isActive: false,
  });

  // Called by SprintDetail to register its undo/redo state
  const registerUndoRedo = useCallback((undoRedoState) => {
    setState(prev => ({
      ...prev,
      ...undoRedoState,
      isActive: true,
    }));
  }, []);

  // Called by SprintDetail when unmounting
  const unregisterUndoRedo = useCallback(() => {
    setState({
      canUndo: false,
      canRedo: false,
      undoCount: 0,
      redoCount: 0,
      isHistoryOpen: false,
      onUndo: () => {},
      onRedo: () => {},
      onToggleHistory: () => {},
      onOpenMissionSearch: null,
      isActive: false,
    });
  }, []);

  return (
    <UndoRedoContext.Provider value={{ ...state, registerUndoRedo, unregisterUndoRedo }}>
      {children}
    </UndoRedoContext.Provider>
  );
}

/**
 * Hook for consuming undo/redo state (used by Sidebar)
 */
export function useUndoRedoContext() {
  return useContext(UndoRedoContext);
}

/**
 * Hook for registering undo/redo state (used by SprintDetail)
 */
export function useRegisterUndoRedo() {
  const { registerUndoRedo, unregisterUndoRedo } = useContext(UndoRedoContext);
  return { registerUndoRedo, unregisterUndoRedo };
}

export default UndoRedoContext;
